










import { ImageTransformHelper } from '@getaccept/editor-lib';
import type { ImageNode, EBlockWidth } from '@getaccept/editor-lib';
import type { PropType, Ref } from '@vue/composition-api';
import { nextTick, defineComponent, computed, ref, watch } from '@vue/composition-api';

export default defineComponent({
  name: 'ImageWithTransforms',
  props: {
    image: { type: Object as PropType<ImageNode>, default: () => ({}) },
    blockWidthClass: {
      type: String as PropType<EBlockWidth>,
      default: null,
    },
    pageWidth: { type: Number, default: 0 },
    isBlockToggled: { type: Boolean },
  },
  setup(props) {
    const imageLoadElement: Ref<HTMLImageElement> = ref(null);
    const insideRef: Ref<HTMLDivElement> = ref(null);
    const containerRef: Ref<HTMLDivElement> = ref(null);

    const scale = computed(
      () => props.image.imageTransforms?.scale || ImageTransformHelper.SCALE_MIN_VALUE
    );
    const ratio = computed(() => scale.value / 100);

    const translateX = computed(() => props.image.imageTransforms?.translateX || 0);
    const translateY = computed(() => props.image.imageTransforms?.translateY || 0);

    const resetImage = async () => {
      await nextTick();
      if (!insideRef.value) {
        return;
      }
      insideRef.value.style.backgroundImage = 'url()';
    };

    const handleImageLoaded = async () => {
      await nextTick();
      if (!containerRef.value) {
        return;
      }

      const { clientWidth: areaWidth, clientHeight: areaHeight } = containerRef.value;
      const { naturalWidth, naturalHeight } = imageLoadElement.value;

      if (!naturalWidth || !areaWidth) {
        return;
      }

      const [initialWidth, initialHeight] = ImageTransformHelper.getInitialImageSize(
        naturalWidth,
        naturalHeight,
        areaWidth,
        areaHeight
      );

      const transformOrigin = ImageTransformHelper.getTransformOrigin(
        initialHeight * ratio.value,
        initialWidth * ratio.value,
        areaHeight,
        areaWidth
      );

      insideRef.value.style.transformOrigin = transformOrigin;
      insideRef.value.style.backgroundImage = `url(${imageLoadElement.value.src})`;
      insideRef.value.style.width = `${initialWidth}px`;
      insideRef.value.style.height = `${initialHeight}px`;
    };

    watch(
      () => props.image.imageTransforms,
      async () => {
        await nextTick();
        if (!insideRef.value) {
          return;
        }

        const translateXCSS = `translateX(${translateX.value / ratio.value}%)`;
        const translateYCSS = `translateY(${translateY.value / ratio.value}%)`;

        insideRef.value.style.transform = `scale(${ratio.value}) ${translateXCSS} ${translateYCSS} translateZ(0)`;
      },
      { immediate: true }
    );

    watch(
      () => props.isBlockToggled,
      () => {
        if (!props.isBlockToggled) {
          return;
        }

        handleImageLoaded();
      }
    );

    watch(
      () => props.pageWidth,
      () => {
        handleImageLoaded();
      }
    );

    watch(
      () => props.image.srcUrl,
      () => {
        imageLoadElement.value = new Image();
        imageLoadElement.value.onload = handleImageLoaded;
        imageLoadElement.value.src = props.image.srcUrl;

        if (!props.image.srcUrl) {
          resetImage();
        }
      },
      { immediate: true }
    );

    return {
      insideRef,
      containerRef,
      resetImage,
      handleImageLoaded,
      imageLoadElement,
    };
  },
});
